// Analyse des exports Extrabat (listing des ventes) et des relevés bancaires.
//
// Fonctions PURES, sans accès réseau ni base : elles reçoivent des feuilles de
// tableur déjà lues (tableaux de lignes, elles-mêmes tableaux de cellules) et
// rendent des enregistrements prêts à insérer.
//
// Partagé VOLONTAIREMENT entre l'application web (import manuel par le
// navigateur) et les scripts serveur (reprise automatique quotidienne) : deux
// analyseurs qui divergeraient produiraient des chiffres différents pour le
// même fichier, sans que personne ne s'en aperçoive.

/** Texte d'une cellule de tableur (les objets non textuels donnent ''). */
export function cellTexte(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

/**
 * « 5 685,00 € » -> 5685 ; cellule vide -> null.
 * Tolère les espaces insécables et les symboles € mal encodés (export Windows).
 */
export function parseMontantCellule(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const brut = cellTexte(v)
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/[€�]/g, '')
    .replace(',', '.');
  if (brut === '' || brut === '-') return null;
  const n = Number.parseFloat(brut);
  return Number.isFinite(n) ? n : null;
}

/** Vraie date du calendrier ? (« 2026-04-31 » et « 2026-02-30 » sont refusés) */
export function dateExiste(iso: string): boolean {
  const [a, m, j] = iso.split('-').map(Number);
  if (!a || !m || !j) return false;
  const d = new Date(a, m - 1, j);
  return d.getFullYear() === a && d.getMonth() === m - 1 && d.getDate() === j;
}

/**
 * Cellule -> date « AAAA-MM-JJ », ou null si ce n'en est pas une.
 *
 * Excel stocke une date sous forme de nombre de jours depuis le 30/12/1899 :
 * on la convertit en arithmétique UTC pure. C'est volontaire — les objets Date
 * construits par les lecteurs de fichiers dépendent du fuseau horaire de la
 * machine, et reculaient d'un jour à Paris (un 1er avril devenait un 31 mars).
 */
export function versDateIso(v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86_400_000);
    const iso = `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    return dateExiste(iso) ? iso : null;
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const m = String(v.getUTCMonth() + 1).padStart(2, '0');
    const d = String(v.getUTCDate()).padStart(2, '0');
    return `${String(v.getUTCFullYear())}-${m}-${d}`;
  }
  const s = cellTexte(v).trim();
  const fr = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s);
  if (fr) {
    const [, jj, mm, aaaa] = fr;
    if (jj && mm && aaaa) {
      const d = `${aaaa}-${mm}-${jj}`;
      return dateExiste(d) ? d : null;
    }
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) {
    const [, a, m, j] = iso;
    if (a && m && j) {
      const d = `${a}-${m}-${j}`;
      return dateExiste(d) ? d : null;
    }
  }
  return null;
}

// ----- Listing des ventes (factures et avoirs Extrabat) ----------------------

export interface LigneVenteImport {
  numero: string;
  type: 'facture' | 'avoir';
  date_emission: string; // AAAA-MM-JJ
  base_55: number;
  base_10: number;
  base_20: number;
  base_exo: number;
}

/**
 * Analyse les feuilles d'un export de ventes Extrabat.
 *
 * Toute ligne dont la 1re colonne est un numéro de pièce (FA… ou AV…) est
 * retenue, quel que soit le découpage du classeur : une feuille par mois ou une
 * feuille unique. Les en-têtes, totaux et lignes de TVA sont ignorés puisqu'ils
 * ne portent pas de numéro de pièce.
 *
 * Colonnes attendues : numéro, date, base HT 5,5 %, 10 %, 20 %, exonérée.
 */
export function analyserListingVentes(feuilles: unknown[][][]): LigneVenteImport[] {
  const lignes: LigneVenteImport[] = [];
  for (const rows of feuilles) {
    for (const row of rows) {
      const numero = cellTexte(row[0]).trim();
      if (!/^(FA|AV)[0-9]/i.test(numero)) continue;
      const dateIso = versDateIso(row[1]);
      if (!dateIso) continue;
      lignes.push({
        numero,
        type: /^AV/i.test(numero) ? 'avoir' : 'facture',
        date_emission: dateIso,
        base_55: parseMontantCellule(row[2]) ?? 0,
        base_10: parseMontantCellule(row[3]) ?? 0,
        base_20: parseMontantCellule(row[4]) ?? 0,
        base_exo: parseMontantCellule(row[5]) ?? 0,
      });
    }
  }
  return lignes;
}
